import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useDessertGate — derives the "can the kid request a dessert right
// now?" boolean from the kid's recent non-dessert food requests.
// Gate opens when the kid has at least one kid_food_requests row
// with status='done' AND request_kind != 'dessert' resolved in the
// last DESSERT_WINDOW_MS. Falls back to closed when there's no
// signal, which is the correct safe default for a "real food before
// dessert" rule.
//
// Heuristic: relies on the parent actually marking requests done
// in the inbox. If the parent never marks anything done, the gate
// never opens. The parent always has an escape hatch — marking any
// pending non-dessert request done opens the window.
//
// Re-ticks once a minute so the unlocked window closes on time
// without needing a user interaction.

const DESSERT_WINDOW_MS = 4 * 60 * 60 * 1000; // 4 hours

export function useDessertGate(kidUserId) {
  const [latestNonDessertResolvedAt, setLatest] = useState(null);
  const chRef = useRef(null);

  const load = useCallback(async () => {
    if (!kidUserId) { setLatest(null); return; }
    const { data, error } = await supabase
      .from("kid_food_requests")
      .select("resolved_at, request_kind, status")
      .eq("kid_user_id", kidUserId)
      .eq("status", "done")
      .neq("request_kind", "dessert")
      .order("resolved_at", { ascending: false })
      .limit(1);
    if (error) {
      console.warn("[dessert-gate] load:", error.message);
      return;
    }
    setLatest(data?.[0]?.resolved_at || null);
  }, [kidUserId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:dessert_gate:${kidUserId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "kid_food_requests" },
        () => load(),
      )
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [kidUserId, load]);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Gate retired 2026-05-27. The "kid must eat real food before
  // dessert" mechanic was never wired to a settable threshold in the
  // parent flow — there was no way to manually open the window
  // outside of resolving a non-dessert request in the inbox. Until
  // a parent-facing override / setting exists, treats are always
  // unlocked. The latestNonDessertResolvedAt + tick wiring stays in
  // place so the hook can be re-gated later by flipping this back.
  // eslint-disable-next-line no-unused-vars
  const _gateInputs = { latestNonDessertResolvedAt, tick };
  const unlocked = true;

  const unlockedUntil = useMemo(() => {
    if (!latestNonDessertResolvedAt) return null;
    const t = new Date(latestNonDessertResolvedAt).getTime();
    if (!Number.isFinite(t)) return null;
    return t + DESSERT_WINDOW_MS;
  }, [latestNonDessertResolvedAt]);

  return { unlocked, unlockedUntil, latestNonDessertResolvedAt };
}
